// Click-only citation chip surface. Operator-rejected the Radix HoverCard
// preview in commits f27ea707..28977bd2 ("Perplexity-Plus full"): the
// preferred UX is "click chip → CitationDetailPanel opens right pane;
// no hover popover". This file is the post-revision form:
//   * NO HoverCard wrapping, NO Radix import, NO portal
//   * Bare <button> with onClick → setActiveCitationIdx(idx)
//   * Bekaert-cyan PERPLEXITY_CHIP_CLASS preserved (chip aesthetics
//     weren't a complaint; only the hover surface)
//   * Unsupported citations (claim_unsupported=true per ADR-082 Phase 5
//     verifier rebind) render NOTHING — operator-explicit 2026-06-02:
//     "issues now with some of the citations showing 'evidence not found'".
//     The CitationChip dispatcher returns null for unsupported; the [N]
//     marker is consumed by MessageRenderer.processText's regex advance
//     so zero visual artifact remains. Rebind data still in PersistedCitationRef
//     for future telemetry.
//
// CitationDetailPanel is responsible for fetching `source_uri` via the
// new GET /v1/tenants/{tid}/documents/{doc_id}/source endpoint and
// rendering the "Open source ↗" link.

import { citationLocation } from "@/lib/citationLocation";
import { useChatStore } from "@/runtime/chatStore";
import { isADR096Citation } from "@/runtime/citationDiscriminator";
import type { PersistedCitationRef } from "@/types";

function shortenDocId(docId: string): string {
  const lastDash = docId.lastIndexOf("-");
  if (lastDash > 0 && docId.length > 40) {
    return docId.slice(0, 16) + "…" + docId.slice(lastDash);
  }
  if (docId.length > 40) return docId.slice(0, 37) + "…";
  return docId;
}

interface Props {
  text: string;
  citations: PersistedCitationRef[];
}

const FOOTNOTE_RE = /\[(\d+)\]/g;

export function InlineFootnotes({ text, citations }: Props) {
  const byIndex = new Map(citations.map((c) => [c.index, c] as const));
  const parts: (string | { idx: number; cite: PersistedCitationRef })[] = [];
  let last = 0;
  for (const match of text.matchAll(FOOTNOTE_RE)) {
    const idx = Number(match[1]);
    const cite = byIndex.get(idx);
    if (!cite) continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push({ idx, cite });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));

  return (
    <span>
      {parts.map((p, i) => {
        if (typeof p === "string") return <span key={i}>{p}</span>;
        return <CitationChip key={i} idx={p.idx} cite={p.cite} />;
      })}
    </span>
  );
}

// `displayIdx` is the VISIBLE chip number. Defaults to `idx` (backward compat
// for InlineFootnotes' own consumers + tests). MessageRenderer passes the
// canonical lowest-index-per-chunk so multiple `[5][10][18]` markers
// rebinding to the same chunk all render as a single canonical "5" chip
// (Defect #1, 2026-06-02 click-only e2e audit). Click handler still
// dispatches the original `idx` so CitationDetailPanel.findCitation
// (search by `c.index === idx`) resolves correctly.
export function CitationChip({
  idx,
  cite,
  displayIdx,
}: {
  idx: number;
  cite: PersistedCitationRef;
  displayIdx?: number;
}) {
  // ADR-096 Phase 3 — cohort-aware claim_unsupported gate.
  //
  // LEGACY (iter 18, 2026-06-02 operator-explicit): hide unsupported
  // citations entirely. The `[N]` marker is consumed by
  // MessageRenderer.processText's regex advance regardless of what
  // CitationChip returns, so null leaves ZERO visual artifact.
  // ADR-082 Phase 5 "claim_unsupported transparency" (rose-styled
  // chip) retired. Verifier rebind data still in PersistedCitationRef.
  //
  // ADR-096: ALWAYS render. claim_unsupported is structurally false
  // on the ADR-096 path (Phase 2 backend bypasses ADR-082 Jaccard
  // rebind when structured_citations_enabled=true), so the legacy
  // gate would naturally evaluate to "render" even without this
  // explicit guard. The cohort branch is the Phase 6 deletion target:
  // when legacy is removed, both the gate AND the explicit ADR-096
  // bypass disappear, leaving the chip to render unconditionally
  // (matching the report_citations_tool schema's guarantee that
  // every emitted PersistedCitationRef is provenance-bound to a cited chunk).
  if (cite.claim_unsupported === true && !isADR096Citation(cite)) {
    return null;
  }
  return <SupportedChip idx={idx} cite={cite} displayIdx={displayIdx} />;
}

// Bekaert-cyan superscript citation pill (--citation-bg fill, --accent-ring
// border deepening to --accent-primary on hover). Since 2026-08-09 this is the
// ONLY citation surface in the answer — the Sources card it used to cohere with
// was deleted. CitationChip.test.tsx pins these substrings:
// rounded-full / text-[var(--citation-fg)] / align-super / font-bold /
// min-h-[16px] / min-w-[16px] + the inline translateY(-2px) style.
const PERPLEXITY_CHIP_CLASS =
  "mx-0.5 inline-flex items-center justify-center rounded-full " +
  "bg-[var(--citation-bg)] hover:bg-[var(--accent-soft)] " +
  "border border-[var(--accent-ring)] hover:border-[var(--accent-primary)] " +
  "px-1.5 py-0 min-h-[16px] min-w-[16px] " +
  "type-caption font-bold text-[var(--citation-fg)] " +
  "align-super cursor-pointer no-underline " +
  "transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] " +
  "focus:outline-none focus-visible:shadow-[var(--elev-focus)]";

function SupportedChip({ idx, cite, displayIdx }: { idx: number; cite: PersistedCitationRef; displayIdx?: number }) {
  const setActiveCitationChunkId = useChatStore((s) => s.setActiveCitationChunkId);
  const location = citationLocation(cite);
  // `title` attribute drives the BROWSER-NATIVE tooltip on hover (one-line
  // text only — NO HoverCard, NO preview, NO popover). This is the only
  // hover affordance preserved per operator: the chip stays informative
  // without re-introducing the rejected preview-card pattern.
  //
  // ADR-097 §3.4 — surface the retrieved chunk `preview` (the source
  // snippet, NOT a model-quoted span) at the top of the hover string for
  // numeric-index cites. This is the user-visible provenance payoff: hover
  // reveals the cited source text, closing the loop without opening the
  // side panel. Legacy cohort falls back to doc_title + page (existing
  // behavior preserved bit-identically).
  const baseTitle = cite.doc_title ?? shortenDocId(cite.doc_id);
  const visibleIdx = displayIdx ?? idx;
  const ariaLabel = location !== null ? `Source ${visibleIdx}, ${location.spoken}` : `Source ${visibleIdx}`;
  const pageLabel = location?.label.startsWith("p.") ? `p. ${location.label.slice(2)}` : location?.label;
  const legacyHoverTitle = pageLabel ? `${baseTitle} · ${pageLabel}` : baseTitle;
  const hoverTitle =
    typeof cite.marker_index === "number" && typeof cite.preview === "string"
      ? `"${cite.preview.slice(0, 120)}" — ${legacyHoverTitle}`
      : legacyHoverTitle;

  return (
    <button
      type="button"
      onClick={() => setActiveCitationChunkId(cite.chunk_id)}
      className={PERPLEXITY_CHIP_CLASS}
      aria-label={ariaLabel}
      title={hoverTitle}
      style={{ transform: "translateY(-2px)" }}
    >
      {visibleIdx}
    </button>
  );
}
